/**
 * What the Equipment tab's sheets add to the page's shared field parts
 * (`fields.tsx` — `FieldLabel`, `FIELD_BOX`, `FIELD_INPUT`):
 *
 * - **`SheetField`** — `FieldLabel` over a control, with ids handed down and
 *   the hint 4px under the box at 11px (Board 6 B's *at the site about 7:30*,
 *   18 H's *internal · used for the visit's cost*), on the boards' own line
 *   height.
 * - **`TextBox`** — a typed value in `FIELD_BOX`, with Board 18 H's faint
 *   faint dollar sign before it and the hour after it.
 * - **`PickField`** — a choice from a list in the same box: *Concrete mixer ⌄*,
 *   *Harrisburg yard*, and a person with their face (*JL Jesse Lutz ⌄*). The
 *   app's menu on a desktop and a sheet from the bottom on a phone
 *   (`build-exactly-as-drawn.md` law 6) — never a native `<select>`.
 * - **`MomentField`** — a day and a time in one box, as Board 6 B writes them
 *   (*Fri Oct 23 · 7:00 AM*), read and written in the visit's own zone. On a
 *   desktop it opens the day (the app's date field) over the quarter hours;
 *   on a phone, the system's own picker under the box (Activity's choice,
 *   `activity/new/when.tsx`).
 */
import { useId, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from 'react'
import { zonedInstant, zonedLocal, type IsoMoment } from '@rowted/shared'
import { cn } from '../lib/cn.js'
import { DateField } from '../components/date-field.js'
import { MenuSelect, type MenuOption } from '../components/menu-select.js'
import { Popover } from '../components/overlays.js'
import { COMMON } from './copy.js'
import { FIELD_BOX, FIELD_INPUT, FieldLabel } from './fields.js'
import { timeIn } from './format.js'
import { VisitGlyph } from './glyphs.js'
import { VISIT_LAYER_OVER } from './sheet.js'

/**
 * A labelled field as Boards 6 B and 18 H draw it: the page's `FieldLabel`
 * (11.5/500, the green required dot, *optional* at the right), the control,
 * and a hint 4px under the box at 11px. The boards set every line at the
 * font's normal height, so the field does too.
 */
export function SheetField({ label, required = false, optional, hint, group = false, className, children }: {
  label: string
  required?: boolean
  /** The faint word at the right — *optional*, from the screen's copy. */
  optional?: string
  hint?: ReactNode
  /** A group of chips or rows, named by `aria-labelledby` rather than `for`. */
  group?: boolean
  className?: string
  children: (ids: { id: string; labelId: string; describedBy?: string }) => ReactNode
}) {
  const id = useId()
  const labelId = `${id}-label`
  const hintId = `${id}-hint`
  return (
    <div className={cn('min-w-0 leading-[normal]', className)}>
      <div id={labelId}>
        <FieldLabel required={required} {...(optional ? { optional } : {})} {...(group ? {} : { htmlFor: id })}>{label}</FieldLabel>
      </div>
      {children({ id, labelId, ...(hint ? { describedBy: hintId } : {}) })}
      {hint && <div id={hintId} className="mt-1 text-[11px] leading-[1.4] text-text-faint">{hint}</div>}
    </div>
  )
}

/**
 * The page's field box (`FIELD_BOX`: 36px, radius 8, 11 either side, 13px) at
 * regular weight with even figures; 44px at 15px on a phone (§19.2's touch
 * size).
 */
export function boxClass(phone: boolean, extra?: string): string {
  return cn(FIELD_BOX, 'font-normal tnum', phone && '!h-11 !text-[15px]', extra)
}

/** A typed value in the field box — Board 18 H's *$ 32.00 / h*, 6 B's *What*. */
export function TextBox({ id, describedBy, value, onChange, phone, prefix, suffix, inputMode, autoFocus, readOnly, onKeyDown, className, strong = false }: {
  id?: string
  describedBy?: string
  value: string
  onChange?: (value: string) => void
  phone: boolean
  /** Faint before the value, 6px from it — *$*. */
  prefix?: string
  /** Faint after the value at 12px, 6px from it — the hour's *per*. */
  suffix?: string
  inputMode?: 'decimal' | 'numeric' | 'text'
  autoFocus?: boolean
  readOnly?: boolean
  onKeyDown?: (event: KeyboardEvent<HTMLInputElement>) => void
  className?: string
  /** The value at 600 — Board 6 B's *What* (*Towable concrete mixer*). */
  strong?: boolean
}) {
  return (
    <span className={boxClass(phone, className)}>
      {prefix && <span aria-hidden className="mr-1.5 text-text-faint">{prefix}</span>}
      <input
        id={id}
        aria-describedby={describedBy}
        value={value}
        inputMode={inputMode}
        autoFocus={autoFocus}
        readOnly={readOnly}
        tabIndex={readOnly ? -1 : undefined}
        onChange={(event) => onChange?.(event.target.value)}
        onKeyDown={onKeyDown}
        className={cn(FIELD_INPUT, 'h-full', strong && 'font-semibold')}
      />
      {suffix && <span aria-hidden className="ml-1.5 text-[12px] text-text-faint">{suffix}</span>}
    </span>
  )
}

export interface PickOption {
  value: string
  label: string
  /** Drawn before the label, on the row and in the box — a face. */
  lead?: ReactNode
}

export function PickField({ id, describedBy, label, value, options, onChange, phone, clearable = false, chevron = true }: {
  id?: string
  describedBy?: string
  label: string
  value: string | null
  options: PickOption[]
  onChange: (value: string | null) => void
  phone: boolean
  clearable?: boolean
  /** The ⌄ at the right — Board 6 B draws it; Board 18 H's *Lives at* does not. */
  chevron?: boolean
}) {
  const menu: MenuOption[] = options.map((option) => ({
    value: option.value,
    label: option.label,
    ...(option.lead ? { glyph: option.lead } : {}),
  }))
  return (
    <MenuSelect
      id={id}
      describedBy={describedBy}
      label={label}
      value={value}
      options={menu}
      onChange={onChange}
      clearable={clearable}
      {...(phone ? { sheet: { title: label } } : {})}
      className={boxClass(phone, 'justify-start gap-2')}
      triggerContent={(chosen) => {
        const lead = options.find((option) => option.value === chosen?.value)?.lead
        return (
          <>
            {lead}
            <span className="min-w-0 flex-1 truncate text-left text-text">{chosen?.label ?? ''}</span>
            {chevron && (
              <span aria-hidden className="ml-auto shrink-0 pl-2 text-text-muted">
                <VisitGlyph name="chevronDown" size={13} stroke={2} />
              </span>
            )}
          </>
        )
      }}
    />
  )
}

/** The quarter hours a pickup or a return is put at, 5:00 AM to 9:00 PM. */
const QUARTERS = Array.from({ length: 65 }, (_, index) => 5 * 60 + index * 15)

/** *Fri Oct 23 · 7:00 AM*. */
export function momentWords(timeZone: string, iso: IsoMoment): string {
  return COMMON.dot(timeIn(timeZone, iso, 'date'), timeIn(timeZone, iso, 'ampm'))
}

export function MomentField({ id, describedBy, label, value, onChange, timeZone, fallbackDay, phone }: {
  id?: string
  describedBy?: string
  label: string
  value: IsoMoment | null
  onChange: (iso: IsoMoment) => void
  timeZone: string
  /** The day the box opens on while it is empty — the rental's first day. */
  fallbackDay: string
  phone: boolean
}) {
  const local = useMemo(() => (value ? zonedLocal(new Date(value), timeZone) : null), [value, timeZone])
  const [open, setOpen] = useState(false)
  const [day, setDay] = useState<string>(local?.date ?? fallbackDay)
  const button = useRef<HTMLButtonElement>(null)
  const words = value ? momentWords(timeZone, value) : ''

  const set = (date: string, minutes: number) => onChange(zonedInstant(date, minutes, timeZone).toISOString())

  if (phone) {
    const text = local
      ? `${local.date}T${String(Math.floor(local.minutes / 60)).padStart(2, '0')}:${String(local.minutes % 60).padStart(2, '0')}`
      : ''
    return (
      <span className={boxClass(true, 'relative')}>
        <span className="truncate">{words}</span>
        <input
          id={id}
          type="datetime-local"
          aria-label={label}
          aria-describedby={describedBy}
          value={text}
          step={900}
          onChange={(event) => {
            const [date, time] = event.target.value.split('T')
            if (!date || !time) return
            const [h = 0, m = 0] = time.split(':').map(Number)
            set(date, h * 60 + m)
          }}
          className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
        />
      </span>
    )
  }

  return (
    <>
      <button
        ref={button}
        id={id}
        type="button"
        aria-label={label}
        aria-describedby={describedBy}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => { setDay(local?.date ?? fallbackDay); setOpen((was) => !was) }}
        className={boxClass(false, cn('text-left', open && 'border-accent shadow-[0_0_0_3px_rgb(var(--color-accent-ring))]'))}
      >
        <span className="min-w-0 flex-1 truncate">{words}</span>
      </button>
      <Popover open={open} onClose={() => setOpen(false)} anchor={button.current} label={label} width={280} snap
        layer={VISIT_LAYER_OVER} maxHeight={360} className="p-2.5">
        <DateField value={day} onChange={(next) => { if (next) setDay(next) }} label={label} size="field" display="day" />
        <div role="listbox" aria-label={label} className="mt-2 grid max-h-[248px] grid-cols-2 gap-1 overflow-y-auto">
          {QUARTERS.map((minutes) => {
            const on = local?.date === day && local.minutes === minutes
            return (
              <button
                key={minutes}
                type="button"
                role="option"
                aria-selected={on}
                onClick={() => { set(day, minutes); setOpen(false) }}
                className={cn('h-[30px] rounded-control px-2 text-left text-[12.5px] font-medium',
                  on ? 'bg-accent-soft text-accent-dark' : 'hover:bg-hover')}
              >
                {timeIn(timeZone, zonedInstant(day, minutes, timeZone).toISOString(), 'ampm')}
              </button>
            )
          })}
        </div>
      </Popover>
    </>
  )
}
